import * as Yup from "yup";
import { validationMessages } from "../common/validationMessages";
import { usaStates } from "../common/usaStates";

export const SERVICE_DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type ServiceDayKey = (typeof SERVICE_DAY_KEYS)[number];

/** Grid Mon–Fri only (Sat/Sun out of scope for Service Days column). */
export const SERVICE_WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
] as const;

export type ServiceWeekdayKey = (typeof SERVICE_WEEKDAY_KEYS)[number];

export const SERVICE_DAY_LABELS: Record<ServiceDayKey, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export const SERVICE_DAY_FULL_LABELS: Record<ServiceDayKey, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

/** Form day key → API serviceMon…serviceSun */
export const SERVICE_DAY_API_FIELDS: Record<
  ServiceDayKey,
  "serviceMon" | "serviceTue" | "serviceWed" | "serviceThu" | "serviceFri" | "serviceSat" | "serviceSun"
> = {
  monday: "serviceMon",
  tuesday: "serviceTue",
  wednesday: "serviceWed",
  thursday: "serviceThu",
  friday: "serviceFri",
  saturday: "serviceSat",
  sunday: "serviceSun",
};

export const EMPTY_SELECT_VALUE = "";

/** AS400 ZDMON–ZDSUN; blank = no value */
export const SERVICE_DAY_AS400_VALUES = [
  "",
  "Y",
  "I",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
] as const;

export type ServiceDayAs400Value = (typeof SERVICE_DAY_AS400_VALUES)[number];

/** Friendly UI labels; storage/API stay Y, I, 0–9, blank */
export const SERVICE_DAY_CODE_LABELS: Record<string, string> = {
  Y: "Same Day",
  I: "Interline",
  "0": "0 days",
  "1": "1 day",
  "2": "2 days",
  "3": "3 days",
  "4": "4 days",
  "5": "5 days",
  "6": "6 days",
  "7": "7 days",
  "8": "8 days",
  "9": "9 days",
};

export const formatServiceDayCodeDisplay = (code?: string | null): string => {
  const c = code?.trim().toUpperCase();
  if (!c || c === "-" || c === "—") return "No Delivery";
  return SERVICE_DAY_CODE_LABELS[c] ?? c;
};

/** Label with muted raw code, e.g. Same Day (Y) */
export const formatServiceDayCodeDisplayWithCode = (code?: string | null): string => {
  const raw = code?.trim() ?? "";
  const display = formatServiceDayCodeDisplay(raw);
  const c = raw.toUpperCase();
  if (!c || c === "-" || c === "—") return display;
  return `${display} (${c})`;
};

// Grid uses formatServiceDayRawDisplay; friendly labels kept for forms / activity log (REVERT friendly grid in routeGuide.tsx if needed).
/** Grid/tooltip: show AS400 character as stored; blank → em dash. */
export const formatServiceDayRawDisplay = (code?: string | null): string => {
  const trimmed = code?.trim();
  if (!trimmed || trimmed === EMPTY_SELECT_VALUE) return "—";
  // Preserve DB value; normalize Y/I to uppercase if API sends mixed case
  const upper = trimmed.toUpperCase();
  if (upper === "Y" || upper === "I") return upper;
  if (/^[0-9]$/.test(trimmed)) return trimmed;
  return trimmed;
};

export const isServiceDayFieldName = (fieldName?: string | null): boolean => {
  if (!fieldName) return false;
  return (Object.values(SERVICE_DAY_API_FIELDS) as string[]).includes(fieldName);
};

export const SERVICE_DAY_AS400_OPTIONS = [
  // REVERT (friendly labels for form):
  // { label: "No Delivery", value: EMPTY_SELECT_VALUE },
  // { label: "Same Day (Y)", value: "Y" },
  // { label: "Interline (I)", value: "I" },
  // ...(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"] as const).map((d) => ({
  //   label: `${SERVICE_DAY_CODE_LABELS[d]} (${d})`,
  //   value: d,
  // })),
  // Match grid: raw AS400 character; blank → em dash
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "Y", value: "Y" },
  { label: "I", value: "I" },
  ...(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"] as const).map((d) => ({
    label: d,
    value: d,
  })),
];

/** Matches backend CoverageStatus enum */
export type CoverageStatus = "SERVICED" | "NOT_SERVICED";

/** AS400 / Route Guide sentinel terminal for Not Serviced ZIPs */
export const NOT_SERVICED_TERMINAL_NUMBER = 99;

export const COVERAGE_STATUS_OPTIONS = [
  { label: "Serviced", value: "SERVICED" },
  { label: "Not Serviced", value: "NOT_SERVICED" },
];

export const DIRECT_FILTER_OPTIONS = [
  { label: "Direct", value: "true" },
  { label: "Indirect", value: "false" },
];

export const ROUTE_GUIDE_FIELD_LABELS = {
  threePlOriginCode: "3PL origin",
  threePlDestCode: "3PL destination",
  extensionCode1: "Routing code 1",
  extensionCode2: "Routing code 2",
} as const;

export const ROUTE_GUIDE_FIELD_HELP = {
  threePlOriginCode:
    "3PL lane for origin (AS400 ZD3PLO). D = Direct, I = Indirect; blank = none.",
  threePlDestCode:
    "3PL lane for destination (AS400 ZD3PLD). D = Direct, I = Indirect; blank = none.",
  extensionCode1: "Optional routing code (AS400 ZDX1), e.g. BGL.",
  extensionCode2: "Optional routing code (AS400 ZDX2), e.g. Ferry, Remote, New York.",
} as const;

export const THREE_PL_LANE_OPTIONS = [
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "Direct (D)", value: "D" },
  { label: "Indirect (I)", value: "I" },
];

export const EXTENSION_CODE_1_OPTIONS = [
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "BGL", value: "BGL" },
];

export const EXTENSION_CODE_2_OPTIONS = [
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "FERRY", value: "FERRY" },
  { label: "REMOTE", value: "REMOTE" },
  { label: "New York", value: "NEWYORK" },
];

/** List/detail row for GET /zip-routing-profiles */
export interface ZipRoutingProfile {
  id: string;
  zipCode?: string;
  zip?: string;
  state: string;
  city?: string | null;
  terminalNumber: number;
  airportCode?: string | null;
  terminalName?: string | null;
  coverageStatus?: CoverageStatus | string | null;
  rateType?: string | null;
  /** AS400 ZDMON–ZDSUN: blank, Y, I, or 0–9 */
  serviceMon?: string | null;
  serviceTue?: string | null;
  serviceWed?: string | null;
  serviceThu?: string | null;
  serviceFri?: string | null;
  serviceSat?: string | null;
  serviceSun?: string | null;
  direct?: boolean;
  quoteAllowed?: boolean;
  threePlOriginCode?: string | null;
  threePlDestCode?: string | null;
  extensionCode1?: string | null;
  extensionCode2?: string | null;
  dateUpdated?: string | null;
  userUpdated?: string | null;
}

export type ZipRoutingProfileFormModel = {
  id?: string;
  zipCode: string;
  state: string;
  city: string;
  terminalNumber: number | null;
  coverageStatus: CoverageStatus;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  direct: boolean;
  quoteAllowed: boolean;
  threePlOriginCode: string;
  threePlDestCode: string;
  extensionCode1: string;
  extensionCode2: string;
};

export interface ZipRoutingProfileListParams {
  search?: string;
  state?: string;
  terminalNumber?: string;
  direct?: string;
  page?: number;
  size?: number;
}

export type ZipRoutingProfileActivityLogAction = "CREATE" | "UPDATE" | "DELETE";

/** Cross-profile activity log row from GET /zip-routing-profiles/activity-log/all */
export interface ZipRoutingProfileActivityLogModel {
  changedAt: string;
  modifiedBy: string;
  action: ZipRoutingProfileActivityLogAction | string;
  /** Legacy multiline description — use only as Details fallback */
  changeDescription?: string | null;
  profileId: string;
  zip?: string | null;
  zipCode?: string | null;
  state: string | null;
  terminalNumber?: number | null;
  airportCode?: string | null;
  terminalName?: string | null;
  previousValue?: string | null;
  newValue?: string | null;
  details?: string | null;
  fieldName?: string | null;
  fieldLabel?: string | null;
}

export interface ZipRoutingProfileActivityLogFilters {
  search?: string;
  action?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const ZIP_ROUTING_ACTIVITY_LOG_ACTION_OPTIONS = [
  { id: "CREATE", name: "CREATE" },
  { id: "UPDATE", name: "UPDATE" },
  { id: "DELETE", name: "DELETE" },
];

/** Display helper: blank/null → em dash */
export const formatActivityLogValue = (value?: string | null): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "—";
};

/** e.g. TN - Tennessee (code only if name unknown) */
export const formatStateDisplay = (stateCode?: string | null): string => {
  const code = stateCode?.trim().toUpperCase();
  if (!code) return "—";
  const match = usaStates.find((s) => s.value === code);
  return match ? `${match.value} - ${match.label}` : code;
};

export const getActivityLogZip = (item: ZipRoutingProfileActivityLogModel): string =>
  formatActivityLogValue(item.zipCode || item.zip);

export const getActivityLogDetails = (item: ZipRoutingProfileActivityLogModel): string => {
  const fromDetails = item.details?.trim();
  if (fromDetails) return fromDetails;
  const fromLabel = item.fieldLabel?.trim();
  if (fromLabel) return fromLabel;
  const legacy = item.changeDescription?.trim();
  if (legacy) return legacy;
  return "—";
};

const normalizeCoverageStatus = (value?: string | null): CoverageStatus =>
  value === "NOT_SERVICED" ? "NOT_SERVICED" : "SERVICED";

const resolveDirect = (profile: ZipRoutingProfile): boolean => {
  if (typeof profile.direct === "boolean") return profile.direct;
  if (profile.rateType === "I") return false;
  if (profile.rateType === "D") return true;
  return true;
};

/** Form stores "" for empty; API uses null */
export const normalizeOptionalCode = (value?: string | null): string =>
  value?.trim() || EMPTY_SELECT_VALUE;

export const formatThreePlLaneCode = (code?: string | null): string => {
  if (!code?.trim()) return "—";
  if (code === "D") return "Direct";
  if (code === "I") return "Indirect";
  return code;
};

export const formatExtensionCode = (code?: string | null): string => {
  if (!code?.trim()) return "—";
  if (code === "NEWYORK") return "New York";
  return code.trim();
};

export const formatQuoteAllowed = (allowed?: boolean): string => (allowed ? "Yes" : "No");

export type TerminalRichDisplayInput = {
  airportCode?: string | null;
  terminalName?: string | null;
  terminalNumber?: number | string | null;
};

/** e.g. ATL — Atlanta — 47 */
export const formatTerminalRichDisplay = (input: TerminalRichDisplayInput): string => {
  const parts = [
    input.airportCode?.trim(),
    input.terminalName?.trim(),
    input.terminalNumber != null && input.terminalNumber !== ""
      ? String(input.terminalNumber)
      : undefined,
  ].filter(Boolean) as string[];
  return parts.length ? parts.join(" — ") : "—";
};

/**
 * Enrich list-row terminal fields from Terminal Master when API omits name/code.
 * Prefer row values; fill gaps from terminalsByNumber keyed by terminalNumber.
 */
export const resolveTerminalDisplayInput = (
  row: Pick<ZipRoutingProfile, "airportCode" | "terminalName" | "terminalNumber">,
  terminalsByNumber: Map<string, { airportCode?: string; terminalName?: string }>
): TerminalRichDisplayInput => {
  const key = row.terminalNumber != null ? String(row.terminalNumber) : "";
  const fromList = key ? terminalsByNumber.get(key) : undefined;
  return {
    airportCode: row.airportCode?.trim() || fromList?.airportCode || null,
    terminalName: row.terminalName?.trim() || fromList?.terminalName || null,
    terminalNumber: row.terminalNumber,
  };
};

export const emptyZipRoutingProfileForm = (): ZipRoutingProfileFormModel => ({
  zipCode: "",
  state: "",
  city: "",
  terminalNumber: null,
  coverageStatus: "SERVICED",
  monday: EMPTY_SELECT_VALUE,
  tuesday: EMPTY_SELECT_VALUE,
  wednesday: EMPTY_SELECT_VALUE,
  thursday: EMPTY_SELECT_VALUE,
  friday: EMPTY_SELECT_VALUE,
  saturday: EMPTY_SELECT_VALUE,
  sunday: EMPTY_SELECT_VALUE,
  direct: true,
  quoteAllowed: false,
  threePlOriginCode: EMPTY_SELECT_VALUE,
  threePlDestCode: EMPTY_SELECT_VALUE,
  extensionCode1: EMPTY_SELECT_VALUE,
  extensionCode2: EMPTY_SELECT_VALUE,
});

/** Normalize AS400 day code for form; blank/null → ""; do not map boolean → Y. */
export const normalizeServiceDayCode = (value?: string | boolean | null): string => {
  if (typeof value === "boolean") {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(
        "[Route Guide] Legacy boolean service day from API; showing blank (no Y conversion)."
      );
    }
    return EMPTY_SELECT_VALUE;
  }
  const raw = value?.trim() ?? "";
  if (!raw) return EMPTY_SELECT_VALUE;
  const upper = raw.toUpperCase();
  if (upper === "Y" || upper === "I") return upper;
  if (/^[0-9]$/.test(raw)) return raw;
  return EMPTY_SELECT_VALUE;
};

export const getProfileServiceDayCode = (
  profile: ZipRoutingProfile,
  day: ServiceDayKey
): string => normalizeServiceDayCode(profile[SERVICE_DAY_API_FIELDS[day]]);

export const mapProfileToForm = (profile: ZipRoutingProfile): ZipRoutingProfileFormModel => {
  const coverageStatus = normalizeCoverageStatus(profile.coverageStatus);
  const terminalNumber =
    coverageStatus === "NOT_SERVICED"
      ? profile.terminalNumber ?? NOT_SERVICED_TERMINAL_NUMBER
      : profile.terminalNumber ?? null;

  return {
    id: profile.id,
    zipCode: profile.zipCode || profile.zip || "",
    state: profile.state || "",
    city: profile.city || "",
    terminalNumber,
    coverageStatus,
    monday: getProfileServiceDayCode(profile, "monday"),
    tuesday: getProfileServiceDayCode(profile, "tuesday"),
    wednesday: getProfileServiceDayCode(profile, "wednesday"),
    thursday: getProfileServiceDayCode(profile, "thursday"),
    friday: getProfileServiceDayCode(profile, "friday"),
    saturday: getProfileServiceDayCode(profile, "saturday"),
    sunday: getProfileServiceDayCode(profile, "sunday"),
    direct: resolveDirect(profile),
    quoteAllowed: !!profile.quoteAllowed,
    threePlOriginCode: normalizeOptionalCode(profile.threePlOriginCode),
    threePlDestCode: normalizeOptionalCode(profile.threePlDestCode),
    extensionCode1: normalizeOptionalCode(profile.extensionCode1),
    extensionCode2: normalizeOptionalCode(profile.extensionCode2),
  };
};

/** Grid helper: one entry per weekday with raw code + display label. */
export const getServiceDayDisplayItems = (
  profile: ZipRoutingProfile
): Array<{ day: ServiceDayKey; label: string; code: string; display: string }> =>
  SERVICE_DAY_KEYS.map((day) => {
    const code = getProfileServiceDayCode(profile, day);
    return {
      day,
      label: SERVICE_DAY_LABELS[day],
      code,
      // display: formatServiceDayCodeDisplay(code),
      display: formatServiceDayRawDisplay(code),
    };
  });

/** Grid Mon–Fri only — always 5 items (blank days stay in the list). */
export const getWeekdayServiceDayDisplayItems = (
  profile: ZipRoutingProfile
): Array<{ day: ServiceWeekdayKey; label: string; code: string; display: string }> =>
  SERVICE_WEEKDAY_KEYS.map((day) => {
    const code = getProfileServiceDayCode(profile, day);
    return {
      day,
      label: SERVICE_DAY_LABELS[day],
      code,
      // display: formatServiceDayCodeDisplay(code),
      display: formatServiceDayRawDisplay(code),
    };
  });

/** Tooltip: Mon Y · Tue — · Wed I · … (Mon–Fri always, raw AS400). */
export const formatServiceDays = (profile: ZipRoutingProfile): string =>
  getWeekdayServiceDayDisplayItems(profile)
    .map(({ label, display }) => `${label} ${display}`)
    .join(" · ");

/** Form blank → API null; otherwise send exact AS400 character. */
export const serviceDayToPayload = (value?: string | null): string | null => {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
};

export const SERVICE_DAYS_REQUIRED_MESSAGE =
  "At least one service day is required when coverage is Serviced";

export const hasAtLeastOneServiceDay = (
  values: Pick<ZipRoutingProfileFormModel, ServiceDayKey>
): boolean =>
  SERVICE_DAY_KEYS.some((day) => (values[day]?.trim() ?? "") !== EMPTY_SELECT_VALUE);

const hasRequiredServiceDaysWhenServiced = function (this: any) {
  const parent = this.parent as ZipRoutingProfileFormModel | undefined;
  if (!parent || parent.coverageStatus !== "SERVICED") return true;
  return hasAtLeastOneServiceDay(parent);
};

const serviceDaySchema = () =>
  Yup.string()
    .oneOf(
      [...SERVICE_DAY_AS400_VALUES],
      "Service day must be blank, Y, I, or 0–9"
    )
    .default(EMPTY_SELECT_VALUE);

export const getZipRoutingProfileValidation = (): Yup.ObjectSchema<ZipRoutingProfileFormModel> =>
  Yup.object({
    id: Yup.string().optional(),
    zipCode: Yup.string()
      .matches(/^\d{5}$/, "ZIP must be 5 digits")
      .required(validationMessages.required),
    state: Yup.string().required(validationMessages.required),
    city: Yup.string().max(64, "The value must be no more than 64 characters").default(""),
    terminalNumber: Yup.number()
      .nullable()
      .when("coverageStatus", {
        is: "SERVICED",
        then: Yup.number()
          .nullable()
          .required("Terminal is required")
          .typeError("Terminal is required"),
        otherwise: Yup.number().nullable().notRequired(),
      }),
    coverageStatus: Yup.mixed<CoverageStatus>()
      .oneOf(["SERVICED", "NOT_SERVICED"], "Coverage status is required")
      .required("Coverage status is required"),
    monday: serviceDaySchema().test(
      "at-least-one-service-day",
      SERVICE_DAYS_REQUIRED_MESSAGE,
      hasRequiredServiceDaysWhenServiced
    ),
    tuesday: serviceDaySchema(),
    wednesday: serviceDaySchema(),
    thursday: serviceDaySchema(),
    friday: serviceDaySchema(),
    saturday: serviceDaySchema(),
    sunday: serviceDaySchema(),
    direct: Yup.boolean().required(),
    quoteAllowed: Yup.boolean().required(),
    threePlOriginCode: Yup.string()
      .max(10)
      .oneOf(["", "D", "I"], "3PL origin must be blank, D (Direct), or I (Indirect)")
      .default(""),
    threePlDestCode: Yup.string()
      .max(10)
      .oneOf(["", "D", "I"], "3PL destination must be blank, D (Direct), or I (Indirect)")
      .default(""),
    extensionCode1: Yup.string()
      .max(10)
      .oneOf(["", "BGL"], "Routing code 1 must be blank or BGL")
      .default(""),
    extensionCode2: Yup.string()
      .max(10)
      .oneOf(
        ["", "FERRY", "REMOTE", "NEWYORK"],
        "Routing code 2 must be blank, FERRY, REMOTE, or NEWYORK"
      )
      .default(""),
  }).defined();
